import { BannerItem } from '../../shared/components/banner-carousel/banner-carousel.component';
import { ChannelData } from '../services/umanager-slug.service';

export interface BannerI18n {
  instant(key: string): string;
}

const BANNER_SLOTS = [
  { key: 'banner1', titleKey: 'pages.home.banners.digitalPresence' },
  { key: 'banner2', titleKey: 'pages.home.banners.productivity' },
  { key: 'banner3', titleKey: 'pages.home.banners.management' },
] as const;

export type BannerClickAction =
  | { type: 'external'; url: string }
  | { type: 'internal'; route: string }
  | { type: 'none' };

export function absoluteHttpsUrl(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'https:' || !url.hostname) {
      return null;
    }
    return trimmed;
  } catch {
    return null;
  }
}

export function inAppRouteFromBannerUrl(
  absoluteUrl: string,
  currentOrigin: string,
  basePath: string
): string | null {
  let target: URL;
  try {
    target = new URL(absoluteUrl);
  } catch {
    return null;
  }
  if (target.origin !== currentOrigin) {
    return null;
  }

  const base = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
  const pathname =
    target.pathname.length > 1 && target.pathname.endsWith('/')
      ? target.pathname.slice(0, -1)
      : target.pathname;

  if (pathname !== base && !pathname.startsWith(`${base}/`)) {
    return null;
  }

  const routePath = pathname === base ? '/' : pathname.slice(base.length);
  return `${routePath}${target.search}${target.hash}`;
}

export function resolveBannerClick(
  banner: Pick<BannerItem, 'externalUrl' | 'route'>
): BannerClickAction {
  if (banner.externalUrl) {
    return { type: 'external', url: banner.externalUrl };
  }
  if (banner.route) {
    return { type: 'internal', route: banner.route };
  }
  return { type: 'none' };
}

export function getDefaultClaroBannerImageUrl(baseName: string, isMobile: boolean): string {
  if (isMobile) {
    return `https://mkt-image-front.s3.us-east-1.amazonaws.com/${baseName}-mobile.png`;
  }
  return `https://mkt-image-front.s3.us-east-1.amazonaws.com/${baseName}.png`;
}

export function parseChannelBannersJson(
  channelData: ChannelData | null | undefined,
  isMobile: boolean,
  i18n: BannerI18n
): BannerItem[] | null {
  if (!channelData?.banners) {
    return null;
  }

  let parsed: Record<string, string>;
  try {
    parsed = JSON.parse(channelData.banners);
  } catch {
    return null;
  }

  const result: BannerItem[] = [];

  BANNER_SLOTS.forEach((item, index) => {
    const desktopUrl = parsed[item.key];
    const mobileUrl = parsed[`${item.key}Mobile`];
    const url = isMobile && mobileUrl ? mobileUrl : desktopUrl;

    if (url) {
      const externalUrl = absoluteHttpsUrl(parsed[`${item.key}Link`]);
      result.push({
        id: String(index + 1),
        imageUrl: url,
        title: i18n.instant(item.titleKey),
        ...(externalUrl ? { externalUrl } : {}),
      });
    }
  });

  return result.length ? result : null;
}

/**
 * Mesma regra da `HomeComponent.updateBanners`: canal customizado ou imagens padrão Claro (S3).
 */
export function buildMarketplaceHomeBanners(
  channelData: ChannelData | null | undefined,
  isMobile: boolean,
  i18n: BannerI18n
): BannerItem[] {
  const fromChannel = parseChannelBannersJson(channelData, isMobile, i18n);
  if (fromChannel && fromChannel.length > 0) {
    return fromChannel;
  }

  return [
    {
      id: '1',
      imageUrl: getDefaultClaroBannerImageUrl('banner-1', isMobile),
      title: i18n.instant('pages.home.banners.digitalPresence'),
      route: '/presenca-digital',
    },
    {
      id: '2',
      imageUrl: getDefaultClaroBannerImageUrl('banner-2', isMobile),
      title: i18n.instant('pages.home.banners.productivity'),
      route: '/colaboracao-e-produtividade',
    },
    {
      id: '3',
      imageUrl: getDefaultClaroBannerImageUrl('banner-3', isMobile),
      title: i18n.instant('pages.home.banners.management'),
      route: '/gestao-e-beneficios-empresariais',
    },
  ];
}
